import { AuthGoBackSVG, ForgotPasswordMailSVG } from "@/assets/svg";
import { forgotPasswordService } from "@/service/auth.service";
import { successToast } from "@/utils/toast";
import { useState } from "react";
import { useForm } from "react-hook-form";

interface ForgotPasswordFormData {
  email: string;
}

interface ForgotPasswordFormProps {
  onGoBack: () => void;
}

const ForgotPasswordForm = ({ onGoBack }: ForgotPasswordFormProps) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ForgotPasswordFormData>({
    mode: "onChange",
    defaultValues: {
      email: "",
    },
  });

  const [resetSent, setResetSent] = useState(false);

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      const {
        data: { message },
      } = await forgotPasswordService(data.email);

      successToast("Success", message);

      setResetSent(true);
    } catch (error) {
      console.error("Password reset request failed:", error);
    }
  };

  return (
    <>
      <div className="mb-3 relative z-50">
        <button
          type="button"
          onClick={onGoBack}
          className="text-[#1E1E1E] text-[18px] font-[600] no-underline hover:underline cursor-pointer flex items-center before:text-lg"
        >
          <AuthGoBackSVG />
          Go back
        </button>
      </div>

      {resetSent ? (
        <div className="flex flex-col items-center text-center">
          <ForgotPasswordMailSVG />

          <h1 className="text-[2rem] text-[#232323] font-bold mb-[12px] mt-[14px]">
            Check your email
          </h1>
          <p className="text-[18px] text-[#969696] font-normal mb-[50px]">
            Check your inbox and click on the link to reset your password.
          </p>

          <button
            onClick={() => {
              setResetSent(false);
            }}
            className="w-full h-[42px] bg-[#73B5C5] text-white rounded-[6px] font-medium"
          >
            Resend
          </button>
        </div>
      ) : (
        <>
          <h1 className="text-[40px] text-[#232323] font-bold mb-2">
            Forgot password
          </h1>
          <p className="text-[18px] text-[#969696] font-normal mb-8">
            Please enter your email address to reset your password
          </p>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="mb-6">
              <label
                htmlFor="email"
                className="block mb-2 text-[#1e1e1e] font-[500] text-[14px]"
              >
                Email address
              </label>
              <input
                type="email"
                id="email"
                placeholder="Enter your email address"
                className={`w-full h-[42px] px-[10px] py-3 border rounded-[6px] text-[14px] text-[#1e1e1e] font-[400] placeholder:text-[#B3B3B3] ${
                  errors.email
                    ? "border-[#FF3B30]"
                    : "border-[#E0E0E0] focus:border-[#73b5c5]"
                } focus:outline-none  focus:shadow-[0_0_0_2px_rgba(0,136,204,0.1)]`}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: "Please enter a valid email address",
                  },
                })}
              />
              {errors.email && (
                <span className="text-[#FF3B30] text-[12px] font-[500] mt-0 block">
                  {errors.email.message}
                </span>
              )}
            </div>

            <button
              type="submit"
              className="mt-6 w-full h-[42px] bg-[#73b5c5] text-white border-none rounded-[4px] text-[18px] font-[600] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed"
              disabled={
                isSubmitting ||
                !isValid ||
                !watch("email") ||
                Object.keys(errors).length > 0
              }
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </form>
        </>
      )}
    </>
  );
};

export default ForgotPasswordForm;
